/**
 * Terminal-state notices for a chat turn.
 *
 * A run ends with `chat.response.completed` or `chat.response.failed`. When
 * the run produced no visible text (an empty synthesis, or a backend error
 * before the first token), the thread used to end silently: the typing
 * indicator went away and nothing else changed, so the user re-asked. These
 * pure helpers decide whether a turn deserves an inline notice row and build
 * it; the streaming hook upserts the row through the regular message list and
 * the conversation view renders it as a compact system notice with a Retry
 * action.
 *
 * Notice rows are session-only pseudo-messages (`role: 'notice'`), like the
 * run-activity groups: they have no DB row and are not rebuilt from history.
 *
 * Keyed by the run's `responseId` (`context.response_id`), never by the
 * assistant bubble id: after a HITL resume the chat retires the suspended
 * message id and renders the resumed reply under a synthetic one, while the
 * response id stays stable across every frame of the run.
 */

export const NOTICE_ROLE = 'notice';

export const NOTICE_KIND = Object.freeze({
  NO_REPLY: 'no_reply',
  FAILED: 'failed',
});

/**
 * Key under which "text was seen" is recorded when the frames of a run carry
 * no response id (legacy / flat payloads).
 */
export const TURN_KEY = '__turn__';

/**
 * Set when text arrived in a frame that carried no response id. A terminal
 * frame that DOES carry one (observed live: empty id on the deltas, real id on
 * `completed`) still counts that text as its own.
 */
export const ANON_KEY = '__anon__';

/**
 * How long a `completed` frame waits for late text before it reads as "no
 * reply". Frames of one run are relayed asynchronously and `completed` has been
 * seen reaching the browser a few ms BEFORE the first delta, with the response
 * id empty on both.
 */
export const NOTICE_GRACE_MS = 1500;

const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);

/** Stable row id for a run's notice (one notice per run, upsert-safe). */
export const noticeIdFor = (responseId, fallbackId = TURN_KEY) =>
  `notice:${str(responseId) ?? str(fallbackId) ?? TURN_KEY}`;

/**
 * Record that visible text (a delta or a non-empty committed frame) was seen
 * for a run. Mutates and returns the given Set. Marks both the run key and the
 * turn key so a terminal frame that omits the response id still finds it.
 * @param {Set<string>} seen
 * @param {string|null|undefined} responseId
 */
export const markTextSeen = (seen, responseId) => {
  const rid = str(responseId);
  if (rid) seen.add(rid);
  else seen.add(ANON_KEY);
  seen.add(TURN_KEY);
  return seen;
};

/**
 * Whether visible text was seen for a run: under its response id, or in frames
 * that carried none (e.g. the deltas while the terminal frame had one). Without
 * a response id it falls back to "any text since the last terminal event". A
 * false negative (no notice) is preferable to a false "No reply" over a
 * visible answer.
 * @param {Set<string>} seen
 * @param {string|null|undefined} responseId
 */
export const hasTextSeen = (seen, responseId) => {
  const rid = str(responseId);
  return rid ? seen.has(rid) || seen.has(ANON_KEY) : seen.has(TURN_KEY);
};

/**
 * Forget a run once it reached a terminal event (keeps the Set bounded). Also
 * clears the turn key: the next turn starts with a clean slate.
 * @param {Set<string>} seen
 * @param {string|null|undefined} responseId
 */
export const clearTextSeen = (seen, responseId) => {
  const rid = str(responseId);
  if (rid) seen.delete(rid);
  seen.delete(TURN_KEY);
  seen.delete(ANON_KEY);
  return seen;
};

/**
 * Normalize the error carried by a `failed` frame. Backends send
 * `data.error: { code, message }`; tolerate a bare string and flat fields.
 * @returns {{ code: string|null, message: string|null }}
 */
export const normalizeTerminalError = (data) => {
  const err = data?.error;
  if (typeof err === 'string') return { code: str(data?.code), message: str(err) };
  return {
    code: str(err?.code) ?? str(data?.code) ?? str(data?.error_code) ?? null,
    message: str(err?.message) ?? str(data?.message) ?? str(data?.error_message) ?? null,
  };
};

/**
 * Decide whether a terminal event deserves a notice row, and build it.
 *
 * - `failed`   → always a `failed` notice (with the backend's error code).
 * - `completed`→ a `no_reply` notice ONLY when no text was seen for the run.
 *   Delta-less workflow runs that pushed committed milestone frames count as
 *   text seen, so they never produce one.
 * - anything else → null.
 *
 * @param {object} args
 * @param {'failed'|'completed'|string} args.event
 * @param {boolean} args.textSeen
 * @param {string|null} [args.responseId]
 * @param {string|null} [args.conversationId]
 * @param {object|null} [args.data] — the frame's data blob (for `error`).
 * @param {string} [args.fallbackId] — id seed when the frame has no response id.
 * @param {string} [args.now] — ISO timestamp (injectable for tests).
 * @returns {null|{ id: string, role: 'notice', kind: 'no_reply'|'failed', code: string|null, message: string|null, responseId: string|null, conversationId: string|null, createdAt: string, timestamp: string }}
 */
export const buildTerminalNotice = ({
  event,
  textSeen,
  responseId = null,
  conversationId = null,
  data = null,
  fallbackId,
  now,
}) => {
  const ev = String(event ?? '').toLowerCase();
  let kind = null;
  let code = null;
  let message = null;
  if (ev === 'failed') {
    kind = NOTICE_KIND.FAILED;
    ({ code, message } = normalizeTerminalError(data));
  } else if (ev === 'completed') {
    if (textSeen) return null;
    kind = NOTICE_KIND.NO_REPLY;
  } else {
    return null;
  }
  const createdAt = now ?? new Date().toISOString();
  return {
    id: noticeIdFor(responseId, fallbackId),
    role: NOTICE_ROLE,
    kind,
    code,
    message,
    responseId: str(responseId),
    conversationId: conversationId ?? null,
    createdAt,
    timestamp: createdAt,
  };
};

/**
 * Upsert a notice row into a message list (pure). One row per id; a `failed`
 * notice is never downgraded to `no_reply` by a later `completed` for the same
 * run. Returns the same array when nothing changes.
 * @param {Array<object>} messages
 * @param {object|null} notice
 */
export const upsertNoticeRow = (messages, notice) => {
  if (!notice) return messages;
  const list = Array.isArray(messages) ? messages : [];
  const idx = list.findIndex((m) => m?.id === notice.id);
  if (idx < 0) return [...list, notice];
  const existing = list[idx];
  if (existing.kind === NOTICE_KIND.FAILED && notice.kind === NOTICE_KIND.NO_REPLY) return list;
  const next = [...list];
  next[idx] = { ...existing, ...notice, createdAt: existing.createdAt ?? notice.createdAt };
  return next;
};

/**
 * Drop a run's `no_reply` notice once text does arrive for it (a late
 * committed frame after `completed`). `failed` notices stay. Pure; returns the
 * same array when nothing changes.
 * A notice is keyed by the run id, or by the message id when the frame had no
 * run id, so the late text is matched by either.
 * @param {Array<object>} messages
 * @param {string|null|undefined} responseId
 * @param {string|null|undefined} [messageId] id of the message the late text belongs to
 */
export const removeNoReplyNotice = (messages, responseId, messageId = null) => {
  const list = Array.isArray(messages) ? messages : [];
  const ids = new Set([noticeIdFor(responseId)]);
  if (str(messageId)) ids.add(noticeIdFor(null, messageId));
  const next = list.filter(
    (m) => !(ids.has(m?.id) && m.role === NOTICE_ROLE && m.kind === NOTICE_KIND.NO_REPLY)
  );
  return next.length === list.length ? list : next;
};

/**
 * Text of the last user message before a notice (the prompt to re-send on
 * Retry). Searches backwards from the notice's position when the notice is in
 * the list, else from the end; optionally restricted to a conversation.
 * @param {Array<object>} messages
 * @param {{ id?: string, conversationId?: string|null }|null} [notice]
 * @returns {string|null}
 */
export const lastUserTextBefore = (messages, notice = null) => {
  const list = Array.isArray(messages) ? messages : [];
  let start = list.length - 1;
  if (notice?.id) {
    const at = list.findIndex((m) => m?.id === notice.id);
    if (at >= 0) start = at - 1;
  }
  const cid = notice?.conversationId ?? null;
  for (let i = start; i >= 0; i -= 1) {
    const m = list[i];
    if (m?.role !== 'user') continue;
    if (cid && m.conversationId && String(m.conversationId) !== String(cid)) continue;
    const text = typeof m.content === 'string' ? m.content.trim() : '';
    if (text) return text;
  }
  return null;
};
